import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Phone } from "lucide-react";
import { Link } from "react-router-dom";

const HERO_IMG =
  "https://images.unsplash.com/photo-1631193816258-28b44b21e78b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200";

const WORDS = ["We", "become", "your", "office."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100vh] min-h-[680px] overflow-hidden" data-testid="hero-section">
      {/* Parallax background image */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Modern office" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050E1A]/85 via-[#050E1A]/55 to-[#050E1A]/95" />
      </motion.div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(28)].map((_, i) => (
          <motion.span
            key={i}
            className="absolute w-1 h-1 rounded-full bg-[#16A34A]/70"
            style={{ left: `${(i * 37) % 100}%`, top: `${(i * 53) % 100}%` }}
            animate={{ y: [-20, 20, -20], opacity: [0.3, 0.9, 0.3] }}
            transition={{ duration: 4 + (i % 4), repeat: Infinity, delay: (i % 5) * 0.4 }}
          />
        ))}
      </div>

      {/* 3D floating cutouts */}
      <motion.div
        animate={{ y: [-10, 10, -10], rotate: [-2, 2, -2] }}
        transition={{ duration: 8, repeat: Infinity }}
        className="hidden md:block absolute top-32 right-12 w-48 rotate-6 rounded-2xl border border-white/15 bg-white/5 backdrop-blur-xl p-4 shadow-2xl"
      >
        <div className="text-[10px] text-emerald-300 font-bold tracking-widest">INVOICE</div>
        <div className="text-white font-display font-black text-2xl mt-1">$4,820</div>
        <div className="mt-3 h-1 w-full bg-white/10 rounded-full overflow-hidden">
          <div className="h-full w-3/4 bg-emerald-400" />
        </div>
        <div className="mt-2 text-[10px] text-white/60">Accounts receivable · paid 75%</div>
      </motion.div>

      <motion.div
        animate={{ y: [10, -12, 10], rotate: [3, -3, 3] }}
        transition={{ duration: 9, repeat: Infinity }}
        className="hidden md:block absolute bottom-32 left-12 w-56 -rotate-3 rounded-2xl border border-white/15 bg-white/5 backdrop-blur-xl p-4 shadow-2xl"
      >
        <div className="text-[10px] text-emerald-300 font-bold tracking-widest">DISPATCH</div>
        <div className="text-white font-semibold mt-1 text-sm">POD collected · Load #4821</div>
        <div className="mt-3 grid grid-cols-3 gap-1">
          {[1,2,3,4,5,6].map(i => (<div key={i} className="h-2 rounded bg-white/15" />))}
        </div>
      </motion.div>

      {/* Content */}
      <motion.div style={{ opacity }} className="relative z-10 h-full container-apex flex flex-col justify-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="overline text-[#16A34A]"
          data-testid="hero-overline"
        >
          Outsourced Office Management · Canada
        </motion.div>

        <h1 className="heading-display text-5xl sm:text-7xl lg:text-[8.5rem] mt-6 max-w-5xl">
          {WORDS.map((w, i) => (
            <span key={i} className="inline-block overflow-hidden align-bottom mr-3">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.15 + i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className={`inline-block ${w === "office." ? "text-[#16A34A]" : ""}`}
              >
                {w}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="mt-8 max-w-2xl text-lg sm:text-xl text-white/80 leading-relaxed"
          data-testid="hero-subhead"
        >
          From customer service and email management to invoicing, document organization, and business administration — Admin Apex Business Solutions gives growing businesses a professional office team without the cost of hiring full-time staff.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.6 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/book" className="btn-accent text-base" data-testid="hero-cta-book">
            Book a Free Consultation <ArrowRight size={16} />
          </Link>
          <a href="#services" className="btn-ghost text-base !text-white !border-white/30 hover:!border-[#16A34A]" data-testid="hero-cta-services">
            Explore Services
          </a>
        </motion.div>

        {/* Trust strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="absolute bottom-10 left-6 right-6 lg:left-12 lg:right-12 flex flex-wrap items-end justify-between gap-6"
        >
          <div className="flex items-center gap-6 text-white/70 text-xs">
            <Phone size={14} className="text-[#16A34A]" />
            <span>Brampton, Ontario · Serving all of Canada</span>
            <a href="tel:+16475107379" data-testid="hero-phone" className="hidden sm:inline hover:text-[#16A34A]">+1 (647) 510-7379</a>
          </div>
          <div className="flex items-center gap-8">
            {["Trucking", "Construction", "Medical", "Real Estate", "Law", "Accounting"].map((t) => (
              <div key={t} className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">
                {t}
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
